import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useRef, useEffect } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Stack from "@mui/material/Stack";

export interface PDFLinkButtonProps {
  url?: string | null;
  noticeNumber?: string | null;
}

export const PDFLinkButton: React.FC<PDFLinkButtonProps> = ({ url, noticeNumber }) => {
  const [isLoading, setIsLoading] = useState<"view" | "download" | false>(false);
  const [, setError] = useState<string | null>(null);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const blobUrlsRef = useRef<string[]>([]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach((blobUrl) => {
        window.URL.revokeObjectURL(blobUrl);
      });
    };
  }, []);

  if (!url) {
    return null;
  }

  const isInternalLink = url.includes("atmts.io");
  const filename = noticeNumber ? `${noticeNumber}.pdf` : "notice.pdf";

  // Handle PDF actions (view or download)
  const handlePDF = async (action: "view" | "download") => {
    setIsLoading(action);
    setError(null);

    try {
      if (!isInternalLink) {
        // For external links, use native browser behavior
        if (action === "download") {
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          link.click();
        } else {
          window.open(url, "_blank");
        }
        return;
      }

      // For internal links, fetch with authentication
      const token = isAuthenticated ? await getAccessTokenSilently() : undefined;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/pdf",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(pdfBlob);

      // Track the blob URL for cleanup on component unmount
      blobUrlsRef.current.push(blobUrl);

      // Small delay to ensure the blob URL is fully processed before opening
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (action === "download") {
        // Trigger download
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.click();
      } else {
        // Open in new tab
        const newWindow = window.open(blobUrl, "_blank");

        // Try to set the document title to the notice number for better identification
        if (newWindow && noticeNumber) {
          newWindow.document.title = noticeNumber;
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        console.error(`Error ${action === "download" ? "downloading" : "opening"} PDF:`, err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack direction="row" spacing={1}>
      <LoadingButton
        onClick={() => handlePDF("view")}
        loading={isLoading === "view"}
        startIcon={<VisibilityIcon />}
        sx={{ textTransform: "none" }}
        variant="contained"
        fullWidth
      >
        View Notice
      </LoadingButton>
      <LoadingButton
        onClick={() => handlePDF("download")}
        loading={isLoading === "download"}
        startIcon={<DownloadIcon />}
        sx={{ textTransform: "none" }}
        variant="contained"
        fullWidth
        color="success"
      >
        Download Notice
      </LoadingButton>
    </Stack>
  );
};

export default PDFLinkButton;
